import { useEffect, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showSnackbarMessage } from 'app/slices/snackbarMessageSlice';
import {
  useLazyGetReceivedMessageDetailsQuery,
  useLazyGetSentMessageDetailsQuery,
  useUpdateReceivedMessageMutation,
} from 'app/features/mail/api/mailApi';

/**
 * Custom hook to manage fetching and updating message details.
 * Handles both sent and received messages, error notifications, and marking as read.
 */
export function useMessageDetails({
  selectedMessage,
  setSelectedMessage,
  selectedTab,
}) {
  const dispatch = useDispatch();
  const [selectedMessageDetails, setSelectedMessageDetails] = useState(null);
  const [updateReceivedMessage] = useUpdateReceivedMessageMutation();
  const [fetchReceivedMessageDetails, receivedMessageDetailsResult] = useLazyGetReceivedMessageDetailsQuery();
  const [fetchSentMessageDetails, fetchSentMessageDetailsResult] = useLazyGetSentMessageDetailsQuery();

  // Fetch message details and handle mark as read
  useEffect(() => {
    if (selectedMessage === null) {
      setSelectedMessageDetails(null);
    } else if (selectedTab === 'sent') {
      fetchSentMessageDetails({ messageId: selectedMessage.id })
        .then((result) => {
          if (result.error) {
            dispatch(showSnackbarMessage({
              message: 'There was an error loading your message. Please try again later.',
              severity: 'error',
            }));
            setSelectedMessageDetails(null);
          } else {
            setSelectedMessageDetails(result.data);
          }
        });
    } else {
      fetchReceivedMessageDetails({ messageId: selectedMessage.id })
        .then((result) => {
          if (result.error) {
            dispatch(showSnackbarMessage({
              message: 'There was an error loading your message. Please try again later.',
              severity: 'error',
            }));
            setSelectedMessageDetails(null);
          } else {
            setSelectedMessageDetails(result.data);
          }
        });
      if (!selectedMessage.read) {
        updateReceivedMessage({ messageId: selectedMessage.id, markAsRead: true })
          .then((result) => {
            if (result.error) {
              dispatch(showSnackbarMessage({
                message: 'There was an error marking your message as read.',
                severity: 'error',
              }));
            }
          });
      }
    }
  }, [selectedMessage]);

  // Expose loading state for details
  const isLoading = receivedMessageDetailsResult.isFetching || fetchSentMessageDetailsResult.isFetching;

  // Helper to close message details
  const closeMessageDetails = useCallback(() => {
    setSelectedMessage(null);
    setSelectedMessageDetails(null);
  }, []);

  return {
    selectedMessageDetails,
    setSelectedMessageDetails,
    isLoading,
    closeMessageDetails,
    fetchReceivedMessageDetails,
  };
}
